/**
 * lib/url-format.ts — Phase 2 single source of truth for product URL formatting.
 *
 * URL format (LOCKED per Phase 2 of the Product Intelligence Layer plan):
 *   /products/{brand-slug}-{type-slug}-{model-lowercase}-fin{NNNNNNN}
 *
 * Examples (FINs are illustrative; each FIN is unique to one product):
 *   /products/whirlpool-top-freezer-refrigerator-wrtx5028pm-fin0001234
 *   /products/ge-deep-freezer-fcm22dlww-fin0067412
 *   /products/samsung-french-door-refrigerator-rf32cg5400sraa-fin0042893
 *
 * Why FIN-suffix (recap from plan):
 *   - Stable forever — FIN never changes, even if model normalization improves
 *   - Self-disambiguating — multiple SKUs with same model get unique URLs
 *   - Industry-standard pattern (Best Buy /6520283, Home Depot /315036094)
 *   - Agents can construct URLs from canonical_products data alone
 *   - Re-extracting models later is safe — the FIN suffix preserves the URL
 *
 * On-page display: the PDP <FinBadge> renders "FIN-0042893" (with hyphen, uppercase)
 * for human readability. The URL slug uses "fin0042893" (lowercase, no hyphen) for
 * clean URL aesthetics.
 *
 * This file mirrors the SQL function `build_product_slug_for_pdp` in migration
 * 20260507_url_format_enforcement.sql — they MUST stay in lockstep. Any change
 * to the type-to-slug map or slugification rules requires both updates.
 */

import { isRetailerHexModel } from '@/lib/model-normalizer';
import {
  TRUE_PART_TYPES,
  FILTER_PRODUCT_TYPES,
  PART_PRODUCT_TYPES,
  isTruePartType,
  isFilterType,
  isPartType,
} from '@/lib/parts/fpn';

// =====================================================
// Type-to-slug mapping — single source of truth.
//
// Keys are the values found in `products.type` (text column with a CHECK constraint
// allowing 22 values total: 16 appliance types + 5 filter types + 1 generic 'cooler').
// Filter types (water-filter, air-filter, condenser-filter, ice-machine-filter,
// inline-filter) are excluded because filter products are pdp_eligible=false per
// migration 20260506d_products_pdp_eligible.sql — they're served via /filters/ hubs.
//
// Values are the user-readable slug component. Some types map to themselves
// (`chest-freezer` → `chest-freezer`); others get a "-refrigerator" or
// "-freezer" suffix where it improves SEO and user clarity.
// =====================================================
export const PRODUCT_TYPE_SLUG_MAP: Record<string, string> = {
  // Refrigerator subtypes (10)
  'top-freezer': 'top-freezer-refrigerator',
  'bottom-freezer': 'bottom-freezer-refrigerator',
  'side-by-side': 'side-by-side-refrigerator',
  'french-door': 'french-door-refrigerator',
  'counter-depth': 'counter-depth-refrigerator',
  'compact': 'compact-refrigerator',
  'built-in': 'built-in-refrigerator',
  'beverage-cooler': 'beverage-refrigerator',
  'wine-cooler': 'wine-refrigerator',
  'refrigerator': 'refrigerator', // generic, used when subtype unknown

  // Freezers (4)
  'chest-freezer': 'chest-freezer',
  'upright-freezer': 'upright-freezer',
  'compact-freezer': 'compact-freezer',
  'freezer': 'freezer', // generic

  // Other appliance categories (3)
  'ice-maker': 'ice-maker',
  'kegerator': 'kegerator',
  'cooler': 'cooler', // generic
};

// =====================================================
// Type-to-display-name mapping — for human-readable category titles.
//
// Mirrors PRODUCT_TYPE_SLUG_MAP above but produces title-case names suitable for
// rendering in the UI (carousel headlines, breadcrumbs, headings). The singular
// form ends in "Refrigerator" / "Freezer" / etc.; the plural form pluralizes the
// final noun. Keys are the same `products.type` values used everywhere else.
//
// This is the single source of truth for category display names. Any UI that
// renders a category headline derived from `products.type` (PDP carousel titles
// like "Best Value Compact Refrigerators", breadcrumbs, etc.) should call
// getCategoryDisplayName / getCategoryDisplayNamePlural rather than rolling
// its own formatter.
// =====================================================
export const PRODUCT_TYPE_DISPLAY_NAME_MAP: Record<string, { singular: string; plural: string }> = {
  // Refrigerator subtypes (10)
  'top-freezer': { singular: 'Top Freezer Refrigerator', plural: 'Top Freezer Refrigerators' },
  'bottom-freezer': { singular: 'Bottom Freezer Refrigerator', plural: 'Bottom Freezer Refrigerators' },
  'side-by-side': { singular: 'Side-by-Side Refrigerator', plural: 'Side-by-Side Refrigerators' },
  'french-door': { singular: 'French Door Refrigerator', plural: 'French Door Refrigerators' },
  'counter-depth': { singular: 'Counter-Depth Refrigerator', plural: 'Counter-Depth Refrigerators' },
  'compact': { singular: 'Compact Refrigerator', plural: 'Compact Refrigerators' },
  'built-in': { singular: 'Built-In Refrigerator', plural: 'Built-In Refrigerators' },
  'beverage-cooler': { singular: 'Beverage Refrigerator', plural: 'Beverage Refrigerators' },
  'wine-cooler': { singular: 'Wine Refrigerator', plural: 'Wine Refrigerators' },
  'refrigerator': { singular: 'Refrigerator', plural: 'Refrigerators' },

  // Freezers (4)
  'chest-freezer': { singular: 'Chest Freezer', plural: 'Chest Freezers' },
  'upright-freezer': { singular: 'Upright Freezer', plural: 'Upright Freezers' },
  'compact-freezer': { singular: 'Compact Freezer', plural: 'Compact Freezers' },
  'freezer': { singular: 'Freezer', plural: 'Freezers' },

  // Other appliance categories (3)
  'ice-maker': { singular: 'Ice Maker', plural: 'Ice Makers' },
  'kegerator': { singular: 'Kegerator', plural: 'Kegerators' },
  'cooler': { singular: 'Cooler', plural: 'Coolers' },

  // Common alternate type values that show up in the wild — mapped to the same
  // display names so callers never have to special-case them.
  'mini-fridge': { singular: 'Mini Fridge', plural: 'Mini Fridges' },
  'beverage-center': { singular: 'Beverage Refrigerator', plural: 'Beverage Refrigerators' },
  'freezerless': { singular: 'Freezerless Refrigerator', plural: 'Freezerless Refrigerators' },
};

/**
 * Returns the singular human-readable display name for a `products.type` value.
 * Examples:
 *   getCategoryDisplayName('compact')        → 'Compact Refrigerator'
 *   getCategoryDisplayName('french-door')    → 'French Door Refrigerator'
 *   getCategoryDisplayName('chest-freezer')  → 'Chest Freezer'
 *
 * Falls back to a title-cased version of the input when the type is unknown,
 * so callers always get a renderable string. Returns 'Refrigerator' for nullish
 * input — this matches the existing default in product-client.tsx.
 */
export function getCategoryDisplayName(type: string | null | undefined): string {
  if (!type) return 'Refrigerator';
  const t = type.toLowerCase().trim();
  const entry = PRODUCT_TYPE_DISPLAY_NAME_MAP[t];
  if (entry) return entry.singular;
  // Fallback: title-case the slug. Always append " Refrigerator" if it doesn't
  // already include a category noun, so headlines never read like a bare adjective.
  const titled = t
    .replace(/[-_]+/g, ' ')
    .split(' ')
    .map(w => (w.length > 0 ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');
  if (/refrigerator|freezer|cooler|fridge|kegerator|maker/i.test(titled)) return titled;
  return `${titled} Refrigerator`;
}

/**
 * Returns the plural human-readable display name for a `products.type` value.
 * Examples:
 *   getCategoryDisplayNamePlural('compact')       → 'Compact Refrigerators'
 *   getCategoryDisplayNamePlural('french-door')   → 'French Door Refrigerators'
 *   getCategoryDisplayNamePlural('mini-fridge')   → 'Mini Fridges'
 *   getCategoryDisplayNamePlural('chest-freezer') → 'Chest Freezers'
 *
 * Falls back to the singular form + 's' when the type is unknown.
 */
export function getCategoryDisplayNamePlural(type: string | null | undefined): string {
  if (!type) return 'Refrigerators';
  const t = type.toLowerCase().trim();
  const entry = PRODUCT_TYPE_DISPLAY_NAME_MAP[t];
  if (entry) return entry.plural;
  // Fallback: pluralize the singular fallback.
  const singular = getCategoryDisplayName(t);
  if (/s$/i.test(singular)) return singular;
  if (/y$/i.test(singular)) return singular.replace(/y$/i, 'ies');
  return `${singular}s`;
}

/**
 * Slug regex (LOCKED). Four hyphen-separated segments, last one matching `fin\d{7}`.
 * The first three segments use [a-z0-9-]+ which permits internal hyphens — important
 * because both brand-slug ("aj-madison") and type-slug ("top-freezer-refrigerator")
 * can contain hyphens. The model component is the only one without internal
 * separators in practice (manufacturer model numbers are alphanumeric).
 */
export const PRODUCT_SLUG_REGEX = /^[a-z0-9-]+-[a-z0-9-]+-[a-z0-9-]+-fin\d{7}$/;

/**
 * The matching FIN-segment regex used for extraction from a slug or URL.
 */
export const FIN_SUFFIX_REGEX = /-fin(\d{7})$/;

/**
 * Validation status enum — exactly mirrors the CHECK constraint on
 * products.seo_slug_validation_status added in the Phase 2 migration.
 */
export type SlugValidationStatus =
  | 'ok'
  | 'unmapped_type'
  | 'missing_fin'
  | 'missing_model'
  | 'collision'
  | 'unmapped'; // bootstrap/default for rows pre-Phase-2

/**
 * Brand slugifier — mirrors the existing SQL function `slugify_brand_name(text)`:
 *   trim(both '-' from regexp_replace(lower(coalesce(input, '')), '[^a-z0-9]+', '-', 'g'))
 *
 * Examples:
 *   "GE Appliances" → "ge-appliances"
 *   "AJ Madison"    → "aj-madison"
 *   "KitchenAid"    → "kitchenaid"
 *   "Sub-Zero"      → "sub-zero"
 *   "Frizzlife Amz" → "frizzlife-amz"  (canonicalize_brand should resolve this to "Frizzlife" first)
 */
export function slugifyBrandName(input: string | null | undefined): string {
  const s = (input ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
  return s.replace(/^-+|-+$/g, '');
}

/**
 * Maps a raw `products.type` value to the user-friendly URL slug component.
 * Returns null when the type is unmapped (e.g. a filter type or some new type
 * we haven't reconciled yet). Caller should treat null as `unmapped_type`.
 */
export function typeToSlug(type: string | null | undefined): string | null {
  if (!type) return null;
  const t = type.toLowerCase().trim();
  return PRODUCT_TYPE_SLUG_MAP[t] ?? null;
}

/**
 * Lowercases and strips characters that don't belong in a URL segment from
 * the manufacturer model. Most manufacturer model numbers are already
 * alphanumeric (e.g. WRT519SZDW, RF32CG5400SRAA, FCM22DLWW), but a few have
 * slashes or dashes we want to normalize.
 *
 * Hyphens are preserved (some real models like "FRSS2623AS-C1" use them).
 * Slashes, spaces, and other non-alphanumeric chars become single hyphens.
 * Result is lowercased.
 *
 * Returns null if the model is empty, missing, or matches the
 * `__PENDING_REVIEW__` placeholder pattern (Phase 1 spawned ~365 of these
 * for canonicals where the OEM model couldn't be resolved auto).
 */
export function normalizeModelForSlug(model: string | null | undefined): string | null {
  if (!model) return null;
  const m = model.trim();
  if (m.length === 0) return null;
  if (m.startsWith('__PENDING_REVIEW__')) return null;
  // Lowercase + collapse non-alphanumeric (except hyphen) into single hyphen
  const slugged = m.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/-+/g, '-');
  const trimmed = slugged.replace(/^-+|-+$/g, '');
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * FRIDGE-144 keyword-stuffing guard — the mint-time check the 07/26 audit
 * asked for ("a recurring check fails the build if a feed import mints one").
 *
 * The defect it prevents: a third-party marketplace title like
 * "SETFEEL ADQ360061 water filter for LT700P ADQ36006101 LFDS22520S KENMORE
 * 9690 46" arriving in the model field and minting a Fridge.com URL made of
 * concatenated competitor model numbers — a machine-generated keyword-stuffing
 * artifact on a site actively proving to a quality classifier that it is not
 * scaled low-value content. 215 such slugs were live when the audit ran; the
 * appliance corpus was clean by 08/24/2026 and this guard is what keeps it so.
 *
 * A "model-shaped token" is a hyphen-separated run that mixes letters and
 * digits and is at least MODEL_SHAPED_TOKEN_MIN_LENGTH characters — the shape
 * of "lmxs28626s", "adq36006101", "lt700p". Purely numeric runs ("30", "2024")
 * and short mixed runs ("4k") do not count, so a real OEM model with a
 * revision suffix ("frss2623as-c1" — one qualifying token) always passes.
 * A genuine single product identity never needs three: three or more DISTINCT
 * model-shaped tokens is a compatibility list, not a model.
 *
 * Mirrored in SQL inside `build_product_slug_for_pdp` (the BEFORE INSERT mint
 * path) — see migration 20260824090000_slug_model_token_stuffing_guard.sql.
 * tests/url-format-display-and-builder.test.ts pins the two copies in
 * lockstep, so drifting either one fails the build.
 */
export const MODEL_TOKEN_STUFFING_THRESHOLD = 3;
export const MODEL_SHAPED_TOKEN_MIN_LENGTH = 5;

/** Distinct model-shaped tokens in an already-normalized slug segment. */
export function countModelShapedTokens(segment: string): number {
  const distinct = new Set<string>();
  for (const token of segment.split('-')) {
    if (
      token.length >= MODEL_SHAPED_TOKEN_MIN_LENGTH &&
      /[a-z]/.test(token) &&
      /[0-9]/.test(token)
    ) {
      distinct.add(token);
    }
  }
  return distinct.size;
}

/**
 * Display-time model sanitizer.
 *
 * Strips Keepa-injected suffixes that aren't part of the real OEM model
 * number. Most common offender (May 10, 2026 user-flagged regression):
 * `_NNNNNN` 4-7 digit tails on commercial / long-tail SKUs (e.g.
 * "C5R9-SB_443616" → "C5R9-SB", "RF23DB9900_201924" → "RF23DB9900").
 * The suffix is a Keepa product-set sequence number that leaked through
 * the Phase 5 keepa-discovery mint into `canonical_products.manufacturer_model`
 * and `products.model`. We strip at DISPLAY time so existing rows look
 * clean without a database backfill.
 *
 * Conservative: only strips `_\d{4,}$` (underscore + 4-or-more digits at
 * the end). Real OEM models occasionally use underscores legitimately —
 * those have non-numeric tails which this regex preserves.
 *
 * Returns the original string if no suffix matched, or empty string for
 * null/empty input. Never returns null (display-safe).
 */
export function cleanModelForDisplay(model: string | null | undefined): string {
  if (!model) return '';
  const m = String(model).trim();
  if (isRetailerHexModel(m)) return '';
  // `__PENDING_REVIEW__<hex>` is the canonical-mint placeholder for "OEM model
  // couldn't be auto-resolved" (same sentinel normalizeModelForSlug rejects).
  // It is INTERNAL and must never reach a user — display nothing instead
  // (2026-07-03 user-flagged: it rendered in the PDP Model row + Expert
  // Verdict sentence).
  if (m.startsWith('__PENDING_REVIEW__')) return '';
  return m.replace(/_\d{4,}$/, '').trim();
}

/**
 * Resolve the model string a shopper should SEE for a product, given the raw
 * `products.model` and the canonical `manufacturer_model`.
 *
 * Rules (2026-06-12 ASIN audit + Rule 6 "ASIN is HIDDEN sitewide" +
 * 2026-07-03 placeholder leak):
 *   - raw is an ASIN (B0XXXXXXXX) and the canonical model is a real model
 *     → prefer the canonical (an ASIN is never a shopper-usable model number).
 *   - raw is the `__PENDING_REVIEW__` placeholder → use the canonical if IT is
 *     displayable, else '' (never show the sentinel).
 *   - the final candidate is STILL an ASIN or placeholder → '' (Rule 6: an
 *     ASIN must never render as a model; the sentinel is internal).
 * The result is passed through cleanModelForDisplay (suffix strip + placeholder
 * guard). Returns '' when nothing displayable exists — callers must render the
 * Model row / verdict phrase conditionally.
 *
 * FRIDGE-110 (08/16/2026): the ASIN test now runs on the value with a leading
 * `<digits>_` FEED PREFIX removed. 412 active rows store their ASIN as
 * "3883_B075QBS8DV" — the CJ feed's own record id joined to the ASIN — and the
 * bare-ASIN test passed that straight through, so an internal identifier
 * reached four shopper-facing render sites: the PDP "Model:" row, the expert
 * verdict sentence, the comparison-card model label, and the meta keywords.
 * Measured live before the fix: 412 of 5,524 active rows, 461 across the table.
 * A prefixed ASIN is still an ASIN, so it is still hidden (Rule 6) — and the
 * prefix is ours, not the manufacturer's, so it can never be shown either.
 */
export function resolveDisplayModel(
  rawModel: string | null | undefined,
  manufacturerModel: string | null | undefined,
): string {
  const raw = String(rawModel ?? '').trim();
  const mfr = String(manufacturerModel ?? '').trim();
  // The feed prefix is stripped ONLY to classify the value, never to display
  // it: "3883_B075QBS8DV" is rejected outright rather than shown as an ASIN.
  const stripFeedPrefix = (s: string) => s.replace(/^\d+_/, '');
  const isAsin = (s: string) => /^B0[A-Z0-9]{8}$/i.test(stripFeedPrefix(s));
  const isPlaceholder = (s: string) => s.startsWith('__PENDING_REVIEW__');
  const mfrDisplayable = mfr.length > 0 && !isPlaceholder(mfr) && !isAsin(mfr) && !isRetailerHexModel(mfr);
  const candidate = (isAsin(raw) || isPlaceholder(raw) || isRetailerHexModel(raw)) && mfrDisplayable ? mfr : raw;
  if (isAsin(candidate) || isPlaceholder(candidate) || isRetailerHexModel(candidate)) return '';
  return cleanModelForDisplay(candidate);
}

/**
 * Format a FIN value for the URL suffix.
 *   "FIN-0042893" → "fin0042893"
 *   "FIN-0000007" → "fin0000007"
 *
 * Returns null if the input doesn't match the expected `FIN-NNNNNNN` shape
 * — defends against accidental garbage propagating into URLs.
 */
export function formatFinForUrl(fin: string | null | undefined): string | null {
  if (!fin) return null;
  const m = fin.trim().match(/^FIN-(\d{7})$/);
  if (!m) return null;
  return `fin${m[1]}`;
}

/**
 * Parse the FIN out of a URL segment.
 *   "fin0042893"  → "FIN-0042893"
 *   "0042893"     → "FIN-0042893"  (also accepted, for /products/fin/{fin} fallback)
 *   anything else → null
 */
export function parseFinFromUrlSegment(seg: string | null | undefined): string | null {
  if (!seg) return null;
  const s = seg.trim().toLowerCase();
  let digits: string | null = null;
  // Both the slug-suffix form (`fin0000768`) and the canonical printed form
  // (`FIN-0000768`, lowercased here) resolve. Until 09/04/2026 the hyphen
  // form — the one /api/products/{fin}/json documents as its URL pattern
  // and the one every page prints — returned null, so the documented URL
  // 404'd (tests/product-json-api-never-silent-404.test.ts).
  const m1 = s.match(/^fin-?(\d{7})$/);
  if (m1) digits = m1[1];
  const m2 = s.match(/^(\d{7})$/);
  if (m2) digits = m2[1];
  if (!digits) return null;
  return `FIN-${digits}`;
}

/**
 * Extract the FIN (in canonical "FIN-NNNNNNN" form) from a full product slug.
 * Returns null if the slug doesn't match the expected shape.
 *
 *   "samsung-french-door-refrigerator-rf32cg5400sraa-fin0042893" → "FIN-0042893"
 *   "whirlpool-wrtx5028pm" (legacy)                                → null
 */
export function extractFinFromSlug(slug: string | null | undefined): string | null {
  if (!slug) return null;
  const m = slug.match(FIN_SUFFIX_REGEX);
  if (!m) return null;
  return `FIN-${m[1]}`;
}

/**
 * True iff the slug matches the locked Phase 2 format.
 */
export function validateProductSlug(slug: string | null | undefined): boolean {
  if (!slug) return false;
  return PRODUCT_SLUG_REGEX.test(slug);
}

// =====================================================
// Main builder
// =====================================================

export interface BuildSlugInput {
  /** products.brand — may be a raw or already-canonicalized display name */
  brand: string | null | undefined;
  /** products.type — value from the CHECK-constrained text column */
  type: string | null | undefined;
  /** canonical_products.manufacturer_model — the OEM model number (NOT an Amazon ASIN) */
  manufacturerModel: string | null | undefined;
  /** canonical_products.fin — "FIN-NNNNNNN" form */
  fin: string | null | undefined;
}

export type SlugBuildResult =
  | { ok: true; slug: string }
  | { ok: false; status: SlugValidationStatus; reason: string };

/**
 * Compose a product URL slug from canonical inputs. This is the single
 * authoritative builder used by both the backfill routine and any future
 * application code that needs to compute a slug.
 *
 * Failure semantics: returns `{ ok: false, status, reason }` rather than
 * throwing. The backfill writes the status into `products.seo_slug_validation_status`
 * so the validation watcher routine can surface failures.
 *
 * The four checks happen in this fixed order so the most actionable failure
 * always wins — a row missing both a model AND a FIN reports "missing_fin"
 * first because there's nothing operator can do until the FIN backfill runs.
 */
export function buildProductSlug(input: BuildSlugInput): SlugBuildResult {
  // 1. FIN must exist + be well-formed (Phase 1 prerequisite)
  const finSegment = formatFinForUrl(input.fin);
  if (!finSegment) {
    return {
      ok: false,
      status: 'missing_fin',
      reason: input.fin
        ? `fin "${input.fin}" doesn't match FIN-NNNNNNN shape`
        : 'fin is null — product is not linked to a canonical_product yet',
    };
  }

  // 2. Type must be in the locked map
  const typeSlug = typeToSlug(input.type);
  if (!typeSlug) {
    return {
      ok: false,
      status: 'unmapped_type',
      reason: input.type
        ? `type "${input.type}" not in PRODUCT_TYPE_SLUG_MAP — add it to lib/url-format.ts AND build_product_slug_for_pdp SQL function`
        : 'type is null',
    };
  }

  // 3. Manufacturer model must exist and not be a placeholder
  const modelSegment = normalizeModelForSlug(input.manufacturerModel);
  if (!modelSegment) {
    return {
      ok: false,
      status: 'missing_model',
      reason: input.manufacturerModel
        ? `manufacturer_model "${input.manufacturerModel}" is empty or a __PENDING_REVIEW__ placeholder — needs human review`
        : 'manufacturer_model is null',
    };
  }

  // 3b. FRIDGE-144: a "model" carrying three or more distinct model-shaped
  // tokens is a compatibility list pasted from a marketplace title, not a
  // model — minting it would put a keyword-stuffed URL on the live domain.
  // Status stays 'missing_model' (the row has no USABLE model and needs the
  // same human review; also the only fitting value the CHECK constraint on
  // products.seo_slug_validation_status allows without a schema change).
  const modelShapedTokens = countModelShapedTokens(modelSegment);
  if (modelShapedTokens >= MODEL_TOKEN_STUFFING_THRESHOLD) {
    return {
      ok: false,
      status: 'missing_model',
      reason: `manufacturer_model "${input.manufacturerModel}" contains ${modelShapedTokens} distinct model-number tokens — a compatibility list, not a model (FRIDGE-144 keyword-stuffing guard); needs human review`,
    };
  }

  // 4. Brand must produce a non-empty slug
  const brandSegment = slugifyBrandName(input.brand);
  if (!brandSegment) {
    return {
      ok: false,
      status: 'unmapped_type', // closest existing status; brand-empty is rare and the type/model states are the priority categories
      reason: 'brand is null or slugifies to empty string',
    };
  }

  const slug = `${brandSegment}-${typeSlug}-${modelSegment}-${finSegment}`;

  // Final validation — should always pass by construction; this catches
  // unexpected edge cases (e.g. a malformed manufacturer model that produced
  // characters outside [a-z0-9-]).
  if (!validateProductSlug(slug)) {
    return {
      ok: false,
      status: 'unmapped_type',
      reason: `built slug "${slug}" failed regex validation — investigate inputs`,
    };
  }

  return { ok: true, slug };
}

/**
 * Builds the canonical_url field that mirrors the slug.
 */
export function buildProductCanonicalUrl(slug: string): string {
  return `/products/${slug}`;
}

// =====================================================
// The NON-APPLIANCE slug authority — parts (FPN) and filters (FFN)
//
// FRIDGE-376 · FRIDGE-388. Everything above this line builds an APPLIANCE
// slug: `<brand>-<appliance-type>-<model>-fin<NNNNNNN>`. It is keyed on a
// FIN, and `typeToSlug` deliberately returns null for every part and filter
// type, so `buildProductSlug` can only ever answer a non-appliance row with
// `unmapped_type` or `missing_fin`.
//
// THAT SINGLE FACT CAUSED BOTH CARDS, measured live 08/24/2026:
//
//   · Where a part or filter row happened to carry a canonical_products row
//     (and therefore a FIN), the SQL mint `build_product_slug_for_pdp` keyed
//     on the CANONICAL row's type — still an appliance type — and stamped an
//     appliance slug on it. 19 rows wear one today: all 13 live true parts
//     (`makspro-french-door-refrigerator-mp-2159061-fin0008831` is a door
//     gasket) and 6 filters. A gasket at a `-french-door-refrigerator-` URL
//     tells a shopper and a crawler the page is a refrigerator, and the
//     `-fin` tail puts an APPLIANCE identifier on a row whose family is
//     forbidden one — "a part carries an FPN and never a FIN".
//
//   · Where it carried no FIN, the builder refused outright and the row got
//     NO slug — so it could never be published. 564 draft filter rows sit at
//     `seo_slug_validation_status = 'missing_fin'` for exactly this reason,
//     every one of them holding a priced offer AND an image. They are not
//     gated on data; they are gated on an identifier their family may not
//     have.
//
// So the fix is one module, not two patches: a builder for the non-appliance
// families that needs NO identifier at all. A part's permanent, searchable
// anchor is its own part number (the `model` column) — WR14X10305, W10383615
// — which is what a shopper actually types. That also keeps the shape valid
// BEFORE the FPN/FFN mints land (staged, awaiting a human — FRIDGE-213), and
// unchanged AFTER them: no slug here ever needs rewriting again.
//
// The family type sets come from lib/parts/fpn.ts — the single source — and
// are never retyped as literals here.
// =====================================================

/**
 * The user-readable URL segment for each NON-appliance type: what the thing
 * IS, never what it fits inside. Keys cover PART_PRODUCT_TYPES exactly
 * (pinned by tests/part-slug-shape.test.ts, so adding a family type without a
 * segment fails the build rather than silently minting a bad URL).
 *
 * The fitment word ("refrigerator") is a QUALIFIER in front of the part noun,
 * never the segment on its own — `refrigerator-gasket` says gasket;
 * `french-door-refrigerator` says refrigerator, which is the defect. Types
 * that already name their own fitment (`ice-machine-filter`, `inline-filter`)
 * do not get a second one.
 */
export const NON_APPLIANCE_TYPE_SLUG_MAP: Record<string, string> = {
  // True parts — (FPN) family, /parts/
  'gasket': 'refrigerator-gasket',
  'sensor': 'refrigerator-sensor',
  'part': 'refrigerator-part',

  // Filters — (FFN) family, /filters/
  'water-filter': 'refrigerator-water-filter',
  'air-filter': 'refrigerator-air-filter',
  'condenser-filter': 'refrigerator-condenser-filter',
  'ice-machine-filter': 'ice-machine-filter',
  'inline-filter': 'inline-filter',
};

/**
 * The noun a slug MUST contain for its own type — the positive half of the
 * guard. Written as alternations because a real catalogue calls a `sensor` a
 * thermistor far more often than a sensor, and a URL that says "thermistor"
 * is doing its job.
 *
 * Why a positive test and not "does the slug contain an appliance word":
 * measured live 08/24/2026, an appliance-WORD match flags 233 filter slugs of
 * which 227 are correct — `waterdrop-...-refrigerator-water-filter-...` names
 * the appliance it FITS, which is exactly what a filter URL should do. A
 * word-match guard would be ~97% false positives on that family, and a guard
 * that is mostly wrong gets silenced. The noun test flags 0 of those 227.
 */
export const NON_APPLIANCE_TYPE_NOUN_PATTERN: Record<string, RegExp> = {
  'gasket': /gasket|seal/,
  'sensor': /sensor|thermistor|thermostat|probe/,
  'part': /part/,
  'water-filter': /filter/,
  'air-filter': /filter/,
  'condenser-filter': /filter/,
  'ice-machine-filter': /filter/,
  'inline-filter': /filter/,
};

/**
 * Maps a NON-appliance `products.type` to its URL segment. Returns null for
 * an appliance type (or an unknown one) — the mirror of `typeToSlug`, which
 * returns null for every part and filter type. Exactly one of the two answers
 * for any given type, which is what keeps the two builders from overlapping.
 */
export function nonApplianceTypeToSlug(type: string | null | undefined): string | null {
  if (!isPartType(type)) return null;
  return NON_APPLIANCE_TYPE_SLUG_MAP[(type as string).trim().toLowerCase()] ?? null;
}

/**
 * Collapse a repeated adjacent token: "continental-refrigerator" + the
 * "refrigerator-gasket" segment would otherwise mint
 * `continental-refrigerator-refrigerator-gasket-2-706`. Adjacent-only and
 * exact-match-only, so a genuine repeat inside a model ("wr14x-wr14x") is the
 * only thing it could ever touch, and that shape is not a real OEM model.
 */
export function collapseAdjacentRepeatedTokens(slug: string): string {
  const out: string[] = [];
  for (const token of slug.split('-')) {
    if (out.length > 0 && out[out.length - 1] === token) continue;
    out.push(token);
  }
  return out.join('-');
}

/**
 * THE RULE, written down before anything was changed (FRIDGE-376 step 2):
 *
 *   a part's or filter's slug is  <brand>-<what it IS>-<its own part number>
 *
 * — brand, then the part type, then the model the row carries, which for a
 * part IS the part number a shopper searches. No FIN: a part carries an FPN
 * and never a FIN, and a URL is the loudest place to get that wrong. No FPN
 * or FFN either — those are not minted yet, and a slug that needs one could
 * not be built today and would have to be rebuilt the day it was.
 *
 * Uniqueness comes from brand + part number, which is how the industry
 * identifies a part. If a brand ever ships two different rows under one part
 * number the caller gets a collision it must resolve — that is a data
 * question, and inventing a numeric suffix to paper over it would put a
 * meaningless token in a permanent URL.
 */
export interface BuildPartSlugInput {
  /** products.brand */
  brand: string | null | undefined;
  /** products.type — must be a part or filter type */
  type: string | null | undefined;
  /** products.model — the part number itself for a part */
  model: string | null | undefined;
}

export function buildPartSlug(input: BuildPartSlugInput): SlugBuildResult {
  // 1. The type must belong to a non-appliance family. An appliance is the
  //    appliance builder's job and must never come through here.
  const typeSlug = nonApplianceTypeToSlug(input.type);
  if (!typeSlug) {
    return {
      ok: false,
      status: 'unmapped_type',
      reason: input.type
        ? `type "${input.type}" is not a part or filter type — appliances use buildProductSlug (PART_PRODUCT_TYPES lives in lib/parts/fpn.ts)`
        : 'type is null',
    };
  }

  // 2. The part number. Same normalizer and same __PENDING_REVIEW__ rejection
  //    as the appliance path.
  const modelSegment = normalizeModelForSlug(input.model);
  if (!modelSegment) {
    return {
      ok: false,
      status: 'missing_model',
      reason: input.model
        ? `model "${input.model}" is empty or a __PENDING_REVIEW__ placeholder — needs human review`
        : 'model is null — a part has no other permanent anchor, so there is no slug to build',
    };
  }

  // 2b. FRIDGE-144 keyword-stuffing guard, unchanged from the appliance path:
  //     a marketplace title pasted into `model` is a compatibility list, not a
  //     part number, and minting it would put a keyword-stuffed URL on the
  //     live domain. This is the shape 564 of the held-back filter rows carry.
  const modelShapedTokens = countModelShapedTokens(modelSegment);
  if (modelShapedTokens >= MODEL_TOKEN_STUFFING_THRESHOLD) {
    return {
      ok: false,
      status: 'missing_model',
      reason: `model "${input.model}" contains ${modelShapedTokens} distinct model-number tokens — a compatibility list, not a part number (FRIDGE-144 keyword-stuffing guard); needs human review`,
    };
  }

  // 3. Brand.
  const brandSegment = slugifyBrandName(input.brand);
  if (!brandSegment) {
    return {
      ok: false,
      status: 'unmapped_type',
      reason: 'brand is null or slugifies to empty string',
    };
  }

  const slug = collapseAdjacentRepeatedTokens(`${brandSegment}-${typeSlug}-${modelSegment}`);

  // 4. The slug must survive its own guard. Anything that fails here would be
  //    a builder bug, and shipping it would grow exactly the defect the guard
  //    exists to stop.
  const finding = assessPartSlug({ slug, type: input.type });
  if (finding) {
    return {
      ok: false,
      status: 'unmapped_type',
      reason: `built slug "${slug}" fails its own guard: ${finding}`,
    };
  }

  return { ok: true, slug };
}

// =====================================================
// The guard
// =====================================================

/**
 * The FIN tail, anywhere it appears at the end of a slug. An appliance slug
 * ends in one BY DESIGN (PRODUCT_SLUG_REGEX above requires it); a part or
 * filter slug ending in one is the FRIDGE-376 defect, exactly and with no
 * false positives — measured live 08/24/2026 it matches 19 non-appliance rows
 * and every one of them is defective.
 */
export const NON_APPLIANCE_SLUG_FORBIDDEN_FIN_TAIL = /-fin\d{7}$/;

/**
 * Returns a human-readable finding when `slug` is not a legal slug for a row
 * of `type`, or null when it is fine. Null means "no finding", so the guard
 * reads as `if (assessPartSlug(...))` at every call site.
 *
 * Two findings, both narrow on purpose:
 *
 *   1. a FIN tail on a non-appliance row — the identifier law, broken in the
 *      most public place there is;
 *   2. a slug that never says what the thing IS — the reader-facing half. A
 *      gasket whose URL contains no gasket-word is mis-describing the page
 *      whatever else it contains.
 *
 * An appliance type returns null: this guard has no opinion about appliance
 * slugs, which PRODUCT_SLUG_REGEX and validateProductSlug already own.
 */
export function assessPartSlug(input: {
  slug: string | null | undefined;
  type: string | null | undefined;
}): string | null {
  const { slug, type } = input;
  if (!isPartType(type)) return null;
  if (!slug) return null; // no slug is a publish question (FRIDGE-388), not a shape defect

  if (NON_APPLIANCE_SLUG_FORBIDDEN_FIN_TAIL.test(slug)) {
    return isFilterType(type)
      ? `carries a FIN tail — a filter carries an (FFN) and never a (FIN)`
      : `carries a FIN tail — a part carries an (FPN) and never a (FIN)`;
  }

  const noun = NON_APPLIANCE_TYPE_NOUN_PATTERN[(type as string).trim().toLowerCase()];
  if (noun && !noun.test(slug)) {
    return `never says what the page is — a "${type}" slug must contain ${noun.source.split('|').map((n) => `"${n}"`).join(' or ')}`;
  }

  return null;
}

/** True iff the slug is a legal slug for a row of that type. */
export function validatePartSlug(slug: string | null | undefined, type: string | null | undefined): boolean {
  if (!isPartType(type)) return false;
  if (!slug) return false;
  return assessPartSlug({ slug, type }) === null;
}

/**
 * The whole non-appliance catalogue in one call, for the nightly guard and for
 * any backfill that wants to know what it is about to touch. Returns one entry
 * per offending row; an empty array is the passing state.
 */
export function findApplianceShapedNonApplianceSlugs<T extends { seo_slug?: string | null; type?: string | null }>(
  rows: readonly T[],
): Array<{ row: T; finding: string }> {
  const out: Array<{ row: T; finding: string }> = [];
  for (const row of rows) {
    const finding = assessPartSlug({ slug: row.seo_slug, type: row.type });
    if (finding) out.push({ row, finding });
  }
  return out;
}

/** Re-exported so a caller needs one import to work the non-appliance families. */
export { TRUE_PART_TYPES, FILTER_PRODUCT_TYPES, PART_PRODUCT_TYPES, isTruePartType, isFilterType, isPartType };
